/**
 * @file `PosturePill` — the per-route publish-trust posture badge
 *       (umbrella #883, part #897).
 * @module @labcraft/admin/ui/kernel/posture-pill
 *
 * Renders one of the five posture kinds (`auto` / `approve` / `remind`
 * / `mixed` / `error`) as a `.pp--*` pill. The vocabulary is reconciled
 * with #349's per-channel routing policy: `auto` ↔ auto-ship, `approve`
 * ↔ require-approval (the amber operator-semantic hue), `remind` ↔
 * draft-and-remind, `mixed` ↔ a per-route blend (e.g. Reddit auto to
 * an owned subreddit, approve to a crosspost), `error` ↔ channel error.
 *
 * Today the Dashboard Needs-you rows are the only consumer; the Social
 * panel retrofit is deferred until #350 lands (it carries the per-route
 * Compose / Channels surfaces the design pairs these with).
 */

import type { JSX } from 'react'
import type { PostureKind } from '../sources/contract.ts'

/** Pure helpers over the posture vocabulary. */
export class PostureView {
  /** Every posture kind, in display order. */
  static readonly KINDS: readonly PostureKind[] = [
    'auto',
    'approve',
    'remind',
    'mixed',
    'error',
  ]

  /**
   * The `.pp--*` class for a posture kind.
   *
   * @param kind - The posture kind.
   * @returns The pill class list.
   */
  static className(kind: PostureKind): string {
    return `pp pp--${kind}`
  }
}

/** Render a posture badge. */
export function PosturePill({
  posture,
}: {
  posture: PostureKind
}): JSX.Element {
  return <span className={PostureView.className(posture)}>{posture}</span>
}
